import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import {
  AlertCircle,
  Bell,
  Briefcase,
  Check,
  Copy,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Globe2,
  KeyRound,
  Mail,
  MessageSquare,
  Plug,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  UserPlus,
  Zap,
} from 'lucide-react';

import PageHeader from '@/components/PageHeader';
import {
  deleteSetting,
  upsertSetting,
  type PlatformSetting,
  type BulkSettingsEntry,
} from '@/lib/operatorApi';
import { formatDateTime } from '@/lib/format';
import { useSettings, type SettingsState } from '@/hooks/useSettings';
import {
  BANNER_FIELDS,
  BRANDING_FIELDS,
  GLOBAL_FLAGS,
  INTEGRATIONS_FIELDS,
  LEGAL_FIELDS,
  SALES_FIELDS,
  SECURITY_FIELDS,
  SIGNUP_FIELDS,
  SMS_FIELDS,
  SMTP_FIELDS,
  SUPPORT_FIELDS,
  YUKASSA_FIELDS,
  globalFlagKey,
  isTrackedKey,
  looksMasked,
  type FieldDef,
} from '@/data/settings';

// ────────────────────────────────────────────────────────────────────────
// Страница «Настройки платформы».
//
// Каждая секция — самостоятельный блок; сохранение идёт через
// bulkUpsertSettings(section, entries) одной транзакцией. Значения читаем
// через useSettings — хук держит единый Map ключ→value и умеет рефрешить
// себя после каждой записи.
// ────────────────────────────────────────────────────────────────────────

type SectionId =
  | 'yukassa'
  | 'email'
  | 'sms'
  | 'signup'
  | 'sales'
  | 'flags'
  | 'banner'
  | 'integrations'
  | 'branding'
  | 'legal'
  | 'support'
  | 'security'
  | 'advanced';

interface SectionDef {
  id: SectionId;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
}

const SECTIONS: SectionDef[] = [
  { id: 'yukassa', label: 'ЮKassa', icon: CreditCard },
  { id: 'email', label: 'Email / SMTP', icon: Mail },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'signup', label: 'Регистрация', icon: UserPlus },
  { id: 'sales', label: 'Отдел продаж', icon: Briefcase },
  { id: 'flags', label: 'Feature flags', icon: Zap },
  { id: 'banner', label: 'Баннер', icon: Bell },
  { id: 'integrations', label: 'Интеграции', icon: Plug },
  { id: 'branding', label: 'Бренд и домены', icon: Globe2 },
  { id: 'legal', label: 'Документы', icon: FileText },
  { id: 'support', label: 'Поддержка', icon: MessageSquare },
  { id: 'security', label: 'Безопасность', icon: ShieldCheck },
  { id: 'advanced', label: 'Расширенные', icon: KeyRound },
];

export default function Settings() {
  const settings = useSettings();

  return (
    <div>
      <PageHeader
        eyebrow="Настройки"
        title="Параметры платформы"
        description="Интеграции, биллинг, feature flags и системные параметры. Изменения применяются ко всем арендаторам."
        actions={
          <button
            type="button"
            className="btn-ghost"
            onClick={() => settings.reload()}
            disabled={settings.loading}
          >
            <RefreshCw
              size={13}
              strokeWidth={1.75}
              className={settings.loading ? 'animate-spin' : ''}
            />
            Обновить
          </button>
        }
      />

      <SectionNav />

      {settings.error && (
        <div className="mx-4 mt-6 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700 md:mx-8">
          {settings.error}
        </div>
      )}

      <div className="space-y-6 px-4 py-6 md:space-y-8 md:px-8 md:py-8">
        <YukassaSection settings={settings} />
        <EmailSection settings={settings} />
        <SmsSection settings={settings} />
        <SignupSection settings={settings} />
        <SalesSection settings={settings} />
        <FeatureFlagsSection settings={settings} />
        <BannerSection settings={settings} />
        <IntegrationsSection settings={settings} />
        <BrandingSection settings={settings} />
        <LegalSection settings={settings} />
        <SupportSection settings={settings} />
        <SecuritySection settings={settings} />
        <AdvancedSection settings={settings} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Sticky-навигация по якорям.
// ────────────────────────────────────────────────────────────────────────

function SectionNav() {
  return (
    <div className="sticky top-14 z-10 border-b border-line bg-paper/90 backdrop-blur md:top-16">
      <div className="flex gap-1 overflow-x-auto px-4 py-3 scrollbar-thin md:px-8">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <a
            key={id}
            href={`#${id}`}
            className="inline-flex shrink-0 items-center gap-1.5 border border-transparent px-3 py-1.5 text-[12px] font-medium text-ink/60 hover:border-line hover:bg-white hover:text-ink"
          >
            <Icon size={13} strokeWidth={1.75} />
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Универсальный каркас секции с сохранением.
// ────────────────────────────────────────────────────────────────────────

function SettingsSection({
  id,
  eyebrow,
  title,
  description,
  icon: Icon,
  children,
  onSave,
  dirty,
  meta,
  footerHint,
  badge,
}: {
  id: SectionId;
  eyebrow: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  children: ReactNode;
  /** Если не передан — блок «Сохранить» не рендерится (секция read-only или со своим CTA). */
  onSave?: () => Promise<void> | void;
  dirty?: boolean;
  meta?: ReactNode;
  footerHint?: ReactNode;
  badge?: ReactNode;
}) {
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!onSave) return;
    setError(null);
    try {
      setSaving(true);
      await onSave();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt((t) => (t && Date.now() - t >= 2400 ? null : t)), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id={id} className="card scroll-mt-32">
      <div className="flex items-start gap-3 border-b border-line px-4 py-4 md:gap-4 md:px-6 md:py-5">
        <div className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center border border-line bg-paper text-ink/70">
          <Icon size={16} strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow">{eyebrow}</span>
            {badge}
            {dirty && (
              <span className="badge-amber">Не сохранено</span>
            )}
          </div>
          <h2 className="mt-1 text-[16px] md:text-[18px] font-medium leading-tight tracking-tight text-ink">
            {title}
          </h2>
          <p className="mt-1.5 max-w-2xl text-[12.5px] leading-relaxed text-ink/60">
            {description}
          </p>
          {meta && (
            <div className="mt-2 text-[11px] text-ink/45 md:hidden">{meta}</div>
          )}
        </div>
        {meta && (
          <div className="hidden shrink-0 pl-4 text-right text-[11px] text-ink/45 md:block">
            {meta}
          </div>
        )}
      </div>

      <div className="px-4 py-5 md:px-6 md:py-6">{children}</div>

      {(onSave || footerHint) && (
        <div className="flex flex-col gap-3 border-t border-line bg-paper/60 px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:px-6 md:py-4">
          <div className="text-[12px] text-ink/50">
            {error ? <span className="text-red-600">{error}</span> : footerHint}
          </div>
          {onSave && (
            <div className="flex items-center gap-3">
              {savedAt && (
                <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-green-600">
                  <Check size={13} strokeWidth={2} /> Сохранено
                </span>
              )}
              <button
                type="button"
                className="btn-primary w-full md:w-auto"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                <Save size={14} strokeWidth={1.75} />
                {saving ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Переиспользуемые мини-компоненты.
// ────────────────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-ink/55">
        {label}
      </div>
      {children}
      {hint && <div className="mt-1.5 text-[11.5px] leading-snug text-ink/50">{hint}</div>}
    </label>
  );
}

function FieldsGrid({ children, cols = 2 }: { children: ReactNode; cols?: 1 | 2 | 3 }) {
  const cls = { 1: '', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3' }[cols];
  return <div className={`grid grid-cols-1 gap-x-6 gap-y-5 ${cls}`}>{children}</div>;
}

function SecretInput({
  value,
  onChange,
  placeholder,
  currentMask,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Если ключ уже есть в БД — покажем маску как подсказку «текущее значение». */
  currentMask?: string | null;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div>
      <div className="relative">
        <input
          type={shown ? 'text' : 'password'}
          className="input pr-20 font-mono text-[12.5px]"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-1 text-[11px] text-ink/60 hover:text-ink"
        >
          {shown ? <EyeOff size={13} strokeWidth={1.75} /> : <Eye size={13} strokeWidth={1.75} />}
          {shown ? 'Скрыть' : 'Показать'}
        </button>
      </div>
      {currentMask && (
        <div className="mt-1 text-[11px] text-ink/50">
          Текущее значение:{' '}
          <code className="font-mono text-ink/70">{currentMask}</code>
          {value ? ' · будет заменено после сохранения' : ' · оставьте поле пустым, чтобы не менять'}
        </div>
      )}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 border border-line bg-white px-4 py-3 transition-colors hover:border-ink/40">
      <div className="min-w-0">
        {label && <div className="text-[13px] font-medium text-ink">{label}</div>}
        {description && (
          <div className="mt-1 text-[12px] leading-snug text-ink/55">{description}</div>
        )}
      </div>
      <span
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-block h-[22px] w-[38px] flex-shrink-0 cursor-pointer transition-colors ${
          checked ? 'bg-orange' : 'bg-line'
        }`}
      >
        <span
          className={`absolute top-[2px] h-[18px] w-[18px] bg-white transition-transform ${
            checked ? 'translate-x-[18px]' : 'translate-x-[2px]'
          }`}
        />
      </span>
    </label>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex border border-line bg-white p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-[12px] font-medium transition-colors ${
            value === o.value ? 'bg-ink text-white' : 'text-ink/65 hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 text-[11px] text-ink/55 hover:text-ink"
    >
      {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.75} />}
      {copied ? 'Скопировано' : 'Копировать'}
    </button>
  );
}

function Callout({ tone, children }: { tone: 'info' | 'warn'; children: ReactNode }) {
  const palette =
    tone === 'warn'
      ? 'border-amber-600/30 bg-amber-50 text-amber-800'
      : 'border-ink/15 bg-paper text-ink/70';
  return (
    <div className={`flex items-start gap-3 border px-3 py-2 text-[12px] ${palette}`}>
      <AlertCircle size={13} strokeWidth={1.75} className="mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Хук-утилита: держит локальный «черновик» секции и выдаёт dirty-флаги.
// ────────────────────────────────────────────────────────────────────────

type SerializableValue = string | number | boolean | null;

function useSectionDraft<T extends Record<string, SerializableValue>>(
  initial: T,
  version: number,
) {
  const [draft, setDraft] = useState<T>(initial);
  const [baseline, setBaseline] = useState<T>(initial);

  // Когда бэкенд вернул свежие данные — подтягиваем их и в baseline, и в draft
  // (но только если пользователь ничего не редактировал). Если draft «грязный»,
  // оставляем черновик, чтобы не потерять ввод.
  useEffect(() => {
    setBaseline(initial);
    setDraft((prev) => (isEqual(prev, baseline) ? initial : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const set = useCallback((patch: Partial<T>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const dirty = useMemo(() => !isEqual(draft, baseline), [draft, baseline]);

  const reset = useCallback(() => setDraft(baseline), [baseline]);

  const markSaved = useCallback((value: T) => {
    setBaseline(value);
    setDraft(value);
  }, []);

  return { draft, set, dirty, reset, markSaved };
}

function isEqual<T extends Record<string, unknown>>(a: T, b: T): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if (a[k] !== b[k]) return false;
  return true;
}

/**
 * Хелпер: по паре «field-def + current» возвращает элемент для bulk-запроса,
 * если значение в черновике отличается от того, что в БД. Для секретов учитывает
 * «маску» — если пользователь не вводил новое значение, ключ не попадает в payload.
 */
function buildEntries<T extends Record<string, SerializableValue>>(
  draft: T,
  baseline: T,
  fields: Record<keyof T, FieldDef>,
  settings: SettingsState,
): BulkSettingsEntry[] {
  const entries: BulkSettingsEntry[] = [];
  for (const k of Object.keys(fields) as (keyof T)[]) {
    const field = fields[k];
    const nextVal = draft[k];
    const prevVal = baseline[k];
    if (field.secret) {
      // Для секретов baseline — это маска. Отправляем только если пользователь
      // реально ввёл новую строку.
      if (typeof nextVal === 'string' && nextVal.trim() !== '') {
        entries.push({ key: field.key, value: nextVal });
      }
      continue;
    }
    if (nextVal !== prevVal) {
      entries.push({ key: field.key, value: nextVal });
    }
  }
  void settings;
  return entries;
}

// ────────────────────────────────────────────────────────────────────────
// ЮKassa.
// ────────────────────────────────────────────────────────────────────────

type YukassaDraft = {
  mode: 'test' | 'live';
  shopId: string;
  secretKey: string; // всегда empty в baseline — только «новое» значение
  returnUrl: string;
  vatCode: '1' | '2' | '3' | '4' | '5' | '6';
  autoCapture: boolean;
  receipt: boolean;
};

function YukassaSection({ settings }: { settings: SettingsState }) {
  const baseline: YukassaDraft = {
    mode: (settings.get<string>(YUKASSA_FIELDS.mode) === 'live' ? 'live' : 'test'),
    shopId: settings.get<string>(YUKASSA_FIELDS.shopId) || '',
    secretKey: '',
    returnUrl: settings.get<string>(YUKASSA_FIELDS.returnUrl) || '',
    vatCode: (String(settings.get(YUKASSA_FIELDS.vatCode)) as YukassaDraft['vatCode']) || '1',
    autoCapture: Boolean(settings.get(YUKASSA_FIELDS.autoCapture)),
    receipt: Boolean(settings.get(YUKASSA_FIELDS.receipt)),
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  const secretMask = settings.isMasked(YUKASSA_FIELDS.secretKey)
    ? String(settings.get(YUKASSA_FIELDS.secretKey))
    : null;

  const webhookUrl = 'https://api.resstro.ru/api/public/yukassa/webhook';

  return (
    <SettingsSection
      id="yukassa"
      eyebrow="Биллинг платформы"
      title="ЮKassa — подписки Resstro"
      description="Реквизиты магазина платформы, через который списываются платежи за подписку SaaS. Платежи гостей в меню арендатор подключает в своей админ-панели отдельно."
      icon={CreditCard}
      badge={
        <span className={draft.mode === 'live' ? 'badge-green' : 'badge-amber'}>
          {draft.mode === 'live' ? 'Live' : 'Test'}
        </span>
      }
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          YUKASSA_FIELDS as Record<keyof YukassaDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('yukassa', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved({ ...draft, secretKey: '' });
      }}
      footerHint="Secret Key шифруется и никогда не отдаётся обратно в UI — после сохранения отображается только маска."
      meta={<FieldMeta field={YUKASSA_FIELDS.shopId} settings={settings} />}
    >
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Segmented
          value={draft.mode}
          onChange={(mode) => set({ mode })}
          options={[
            { value: 'test', label: 'Тестовый режим' },
            { value: 'live', label: 'Боевой режим' },
          ]}
        />
        <span className="text-[12px] text-ink/50">
          В тестовом режиме используются тестовые карты ЮKassa и реальные деньги не списываются.
        </span>
      </div>

      <FieldsGrid>
        <Field label="Shop ID" hint="Идентификатор магазина в личном кабинете ЮKassa.">
          <input
            className="input font-mono text-[13px]"
            placeholder="012345"
            value={draft.shopId}
            onChange={(e) => set({ shopId: e.target.value })}
          />
        </Field>

        <Field
          label="Secret Key"
          hint="Секретный ключ магазина (live_* или test_*). Хранится только на бэкенде."
        >
          <SecretInput
            value={draft.secretKey}
            onChange={(secretKey) => set({ secretKey })}
            placeholder={draft.mode === 'live' ? 'live_xxxxxxxxxxxxxxxx' : 'test_xxxxxxxxxxxxxxxx'}
            currentMask={secretMask}
          />
        </Field>

        <Field label="URL возврата" hint="Куда возвращается пользователь после оплаты подписки." className="md:col-span-2">
          <input
            className="input"
            value={draft.returnUrl}
            onChange={(e) => set({ returnUrl: e.target.value })}
          />
        </Field>

        <Field label="Webhook URL" hint="Скопируйте и вставьте этот адрес в разделе «HTTP-уведомления» ЛК ЮKassa." className="md:col-span-2">
          <div className="flex items-center gap-2">
            <input readOnly className="input flex-1 font-mono text-[12.5px]" value={webhookUrl} />
            <CopyButton value={webhookUrl} />
          </div>
        </Field>

        <Field label="Ставка НДС в чеке (54-ФЗ)">
          <select
            className="input"
            value={draft.vatCode}
            onChange={(e) => set({ vatCode: e.target.value as YukassaDraft['vatCode'] })}
          >
            <option value="1">1 — Без НДС</option>
            <option value="2">2 — НДС 0%</option>
            <option value="3">3 — НДС 10%</option>
            <option value="4">4 — НДС 20%</option>
            <option value="5">5 — НДС 10/110</option>
            <option value="6">6 — НДС 20/120</option>
          </select>
        </Field>

        <Field label="Поведение платежа">
          <div className="space-y-2">
            <Toggle
              checked={draft.autoCapture}
              onChange={(autoCapture) => set({ autoCapture })}
              label="Автосписание после холда"
              description="Платёж подтверждается автоматически. Отключите, если нужен двух-шаговый hold → capture."
            />
            <Toggle
              checked={draft.receipt}
              onChange={(receipt) => set({ receipt })}
              label="Отправлять чек по 54-ФЗ"
              description="Фискальный чек формируется ЮKassa и отправляется клиенту на email."
            />
          </div>
        </Field>
      </FieldsGrid>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Email / SMTP.
// ────────────────────────────────────────────────────────────────────────

type EmailDraft = {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
};

function EmailSection({ settings }: { settings: SettingsState }) {
  const baseline: EmailDraft = {
    host: settings.get<string>(SMTP_FIELDS.host) || '',
    port: Number(settings.get(SMTP_FIELDS.port)) || 465,
    secure: Boolean(settings.get(SMTP_FIELDS.secure)),
    user: settings.get<string>(SMTP_FIELDS.user) || '',
    password: '',
    fromName: settings.get<string>(SMTP_FIELDS.fromName) || '',
    fromEmail: settings.get<string>(SMTP_FIELDS.fromEmail) || '',
    replyTo: settings.get<string>(SMTP_FIELDS.replyTo) || '',
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);
  const mask = settings.isMasked(SMTP_FIELDS.password)
    ? String(settings.get(SMTP_FIELDS.password))
    : null;
  const [testTo, setTestTo] = useState('');

  return (
    <SettingsSection
      id="email"
      eyebrow="Транзакционные письма"
      title="SMTP и отправитель"
      description="Параметры для кодов регистрации, писем входа, квитанций и уведомлений арендаторам."
      icon={Mail}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          SMTP_FIELDS as Record<keyof EmailDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('smtp', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved({ ...draft, password: '' });
      }}
      footerHint="При смене пароля приложения новое значение шифруется. Проверяйте соединение до сохранения."
    >
      <FieldsGrid cols={3}>
        <Field label="SMTP host" className="md:col-span-2">
          <input
            className="input"
            value={draft.host}
            onChange={(e) => set({ host: e.target.value })}
          />
        </Field>
        <Field label="Порт">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.port)}
            onChange={(e) => set({ port: Number(e.target.value) || 0 })}
          />
        </Field>

        <Field label="Логин / email" className="md:col-span-2">
          <input
            className="input"
            value={draft.user}
            onChange={(e) => set({ user: e.target.value })}
          />
        </Field>
        <Field label="SSL/TLS">
          <Toggle
            checked={draft.secure}
            onChange={(secure) => set({ secure })}
            label={draft.secure ? 'Включён (465)' : 'STARTTLS (587)'}
          />
        </Field>

        <Field label="Пароль приложения" className="md:col-span-3">
          <SecretInput
            value={draft.password}
            onChange={(password) => set({ password })}
            placeholder="app-password"
            currentMask={mask}
          />
        </Field>
      </FieldsGrid>

      <div className="my-6 border-t border-line" />

      <FieldsGrid cols={3}>
        <Field label="От кого — имя">
          <input
            className="input"
            value={draft.fromName}
            onChange={(e) => set({ fromName: e.target.value })}
          />
        </Field>
        <Field label="От кого — email">
          <input
            className="input"
            value={draft.fromEmail}
            onChange={(e) => set({ fromEmail: e.target.value })}
          />
        </Field>
        <Field label="Reply-To" hint="На какой адрес пойдут ответы пользователей.">
          <input
            className="input"
            value={draft.replyTo}
            onChange={(e) => set({ replyTo: e.target.value })}
          />
        </Field>
      </FieldsGrid>

      <div className="mt-6 border border-line bg-paper/60 p-4">
        <div className="text-[12px] font-medium text-ink/70">Тестовое письмо</div>
        <p className="mt-1 text-[11.5px] text-ink/55">
          Отправим письмо с шаблоном «код подтверждения», чтобы убедиться, что DKIM/SPF настроены
          корректно. Тестовая отправка использует текущие (сохранённые) параметры SMTP, а не черновик.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            type="email"
            className="input max-w-sm"
            placeholder="test@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
          <button type="button" className="btn-outline" disabled={!testTo.includes('@')}>
            <Mail size={13} strokeWidth={1.75} />
            Отправить тест
          </button>
        </div>
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// SMS.
// ────────────────────────────────────────────────────────────────────────

type SmsDraft = {
  enabled: boolean;
  provider: 'smsru' | 'smsc' | 'messagebird';
  apiKey: string;
  sender: string;
};

function SmsSection({ settings }: { settings: SettingsState }) {
  const baseline: SmsDraft = {
    enabled: Boolean(settings.get(SMS_FIELDS.enabled)),
    provider: (settings.get<string>(SMS_FIELDS.provider) as SmsDraft['provider']) || 'smsru',
    apiKey: '',
    sender: settings.get<string>(SMS_FIELDS.sender) || '',
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);
  const mask = settings.isMasked(SMS_FIELDS.apiKey) ? String(settings.get(SMS_FIELDS.apiKey)) : null;

  return (
    <SettingsSection
      id="sms"
      eyebrow="Уведомления"
      title="SMS-провайдер"
      description="Канал резервной доставки кодов входа и регистрации, когда email недоступен или отложен."
      icon={MessageSquare}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          SMS_FIELDS as Record<keyof SmsDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('sms', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved({ ...draft, apiKey: '' });
      }}
      footerHint="При выключенном провайдере коды шлются только email. Это безопасно, но медленнее."
    >
      <div className="mb-6">
        <Toggle
          checked={draft.enabled}
          onChange={(enabled) => set({ enabled })}
          label="Включить SMS-канал"
          description="После включения коды регистрации дублируются на номер телефона при наличии."
        />
      </div>

      <FieldsGrid>
        <Field label="Провайдер">
          <select
            className="input"
            value={draft.provider}
            onChange={(e) => set({ provider: e.target.value as SmsDraft['provider'] })}
            disabled={!draft.enabled}
          >
            <option value="smsru">SMS.ru</option>
            <option value="smsc">SMSC.ru</option>
            <option value="messagebird">MessageBird</option>
          </select>
        </Field>
        <Field
          label="Имя отправителя"
          hint="Alphanumeric, до 11 символов. Должно быть зарегистрировано у оператора."
        >
          <input
            className="input"
            value={draft.sender}
            onChange={(e) => set({ sender: e.target.value })}
            maxLength={11}
            disabled={!draft.enabled}
          />
        </Field>
        <Field label="API-ключ" className="md:col-span-2">
          <SecretInput
            value={draft.apiKey}
            onChange={(apiKey) => set({ apiKey })}
            placeholder="xxxx-xxxx-xxxx"
            currentMask={mask}
          />
        </Field>
      </FieldsGrid>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Регистрация.
// ────────────────────────────────────────────────────────────────────────

type SignupDraft = {
  trialDays: number;
  codeTtlMin: number;
  cooldownSec: number;
  maxAttempts: number;
  requirePhone: boolean;
  requireConsent: boolean;
  autoCreateDemo: boolean;
};

function SignupSection({ settings }: { settings: SettingsState }) {
  const baseline: SignupDraft = {
    trialDays: Number(settings.get(SIGNUP_FIELDS.trialDays)) || 3,
    codeTtlMin: Number(settings.get(SIGNUP_FIELDS.codeTtlMin)) || 10,
    cooldownSec: Number(settings.get(SIGNUP_FIELDS.cooldownSec)) || 60,
    maxAttempts: Number(settings.get(SIGNUP_FIELDS.maxAttempts)) || 5,
    requirePhone: Boolean(settings.get(SIGNUP_FIELDS.requirePhone)),
    requireConsent: Boolean(settings.get(SIGNUP_FIELDS.requireConsent)),
    autoCreateDemo: Boolean(settings.get(SIGNUP_FIELDS.autoCreateDemo)),
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="signup"
      eyebrow="Онбординг"
      title="Регистрация и пробный период"
      description="Длительность триала, параметры OTP-кодов и требования к форме регистрации на лендинге."
      icon={UserPlus}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          SIGNUP_FIELDS as Record<keyof SignupDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('signup', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved(draft);
      }}
      footerHint="Изменения применятся к новым регистрациям. Уже активные триалы не пересчитываются."
    >
      <FieldsGrid cols={3}>
        <Field label="Пробный период (дней)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.trialDays)}
            onChange={(e) => set({ trialDays: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="TTL кода (минут)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.codeTtlMin)}
            onChange={(e) => set({ codeTtlMin: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Cooldown между запросами (сек)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.cooldownSec)}
            onChange={(e) => set({ cooldownSec: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field
          label="Попыток ввода кода"
          hint="После превышения — выдаём новый код."
          className="md:col-span-3"
        >
          <input
            className="input max-w-[160px]"
            inputMode="numeric"
            value={String(draft.maxAttempts)}
            onChange={(e) => set({ maxAttempts: Number(e.target.value) || 0 })}
          />
        </Field>
      </FieldsGrid>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <Toggle
          checked={draft.requirePhone}
          onChange={(requirePhone) => set({ requirePhone })}
          label="Телефон обязателен"
          description="Требовать номер при регистрации."
        />
        <Toggle
          checked={draft.requireConsent}
          onChange={(requireConsent) => set({ requireConsent })}
          label="Согласие на обработку ПД"
          description="Чек-бокс под офертой/152-ФЗ."
        />
        <Toggle
          checked={draft.autoCreateDemo}
          onChange={(autoCreateDemo) => set({ autoCreateDemo })}
          label="Демо-меню после регистрации"
          description="Автоматически создавать 1 категорию и 3 блюда, чтобы кабинет не был пустым."
        />
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Отдел продаж: доля подписок в сейф отдела.
// ────────────────────────────────────────────────────────────────────────

type SalesDraft = {
  /** Строкой: в поле можно ввести «35,5». */
  safePercent: string;
  safeStart: string;
};

function SalesSection({ settings }: { settings: SettingsState }) {
  const rawPercent = Number(settings.get(SALES_FIELDS.safePercent));
  const rawStart = settings.get(SALES_FIELDS.safeStart);
  const baseline: SalesDraft = {
    safePercent: String(Number.isFinite(rawPercent) ? rawPercent : SALES_FIELDS.safePercent.default),
    safeStart: typeof rawStart === 'string' && rawStart ? rawStart : String(SALES_FIELDS.safeStart.default),
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="sales"
      eyebrow="Менеджеры"
      title="Отдел продаж"
      description="Какая доля оплат подписок уходит в сейф отдела. Сводку и остаток сейфа видит руководитель отдела продаж в manager.resstro.ru."
      icon={Briefcase}
      dirty={dirty}
      onSave={async () => {
        const percent = Number(draft.safePercent.trim().replace(',', '.'));
        if (!draft.safePercent.trim() || !Number.isFinite(percent) || percent < 0 || percent > 100) {
          throw new Error('Процент — число от 0 до 100');
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.safeStart)) throw new Error('Укажите дату начала учёта');
        const entries: BulkSettingsEntry[] = [];
        if (draft.safePercent !== baseline.safePercent) {
          entries.push({ key: SALES_FIELDS.safePercent.key, value: percent });
        }
        if (draft.safeStart !== baseline.safeStart) {
          entries.push({ key: SALES_FIELDS.safeStart.key, value: draft.safeStart });
        }
        if (entries.length === 0) return;
        const r = await settings.saveSection('sales', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved({ safePercent: String(percent), safeStart: draft.safeStart });
      }}
      footerHint="Новый процент действует для следующих оплат. Прошлые поступления не пересчитываются."
    >
      <FieldsGrid cols={2}>
        <Field
          label="В сейф с каждой оплаты подписки, %"
          hint="Со всех оплат компании, не только с заведений менеджеров. 0 — сейф не пополняется."
        >
          <input
            className="input max-w-[160px]"
            inputMode="decimal"
            value={draft.safePercent}
            onChange={(e) => set({ safePercent: e.target.value })}
          />
        </Field>
        <Field
          label="Учитывать оплаты с"
          hint="Оплаты раньше этой даты в сейф не идут. Если сдвинуть дату назад, доли за прошлые оплаты догонятся в течение часа."
        >
          <input
            className="input max-w-[200px]"
            type="date"
            value={draft.safeStart}
            onChange={(e) => e.target.value && set({ safeStart: e.target.value })}
          />
        </Field>
      </FieldsGrid>
      <div className="mt-5">
        <Callout tone="info">
          Остаток сейфа = поступления с даты учёта − расходы отдела «из сейфа» − выплаты менеджерам, отмеченные в их
          карточках.
        </Callout>
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Feature flags.
// ────────────────────────────────────────────────────────────────────────

function FeatureFlagsSection({ settings }: { settings: SettingsState }) {
  const baselineMap = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const f of GLOBAL_FLAGS) {
      const key = globalFlagKey(f.key);
      m[f.key] = Boolean(settings.raw.get(key));
    }
    return m;
  }, [settings.raw]);

  const [state, setState] = useState<Record<string, boolean>>(baselineMap);
  useEffect(() => {
    setState(baselineMap);
  }, [baselineMap]);

  const dirty = useMemo(
    () => GLOBAL_FLAGS.some((f) => state[f.key] !== baselineMap[f.key]),
    [state, baselineMap],
  );

  return (
    <SettingsSection
      id="flags"
      eyebrow="Поэтапная выкатка"
      title="Глобальные feature flags"
      description="Тумблеры видны всем арендаторам. Для флагов конкретного ресторана — вкладка Арендаторы → Flags."
      icon={Zap}
      dirty={dirty}
      onSave={async () => {
        const entries: BulkSettingsEntry[] = GLOBAL_FLAGS
          .filter((f) => state[f.key] !== baselineMap[f.key])
          .map((f) => ({ key: globalFlagKey(f.key), value: state[f.key] }));
        const r = await settings.saveSection('feature', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
      }}
    >
      <div className="space-y-2">
        {GLOBAL_FLAGS.map((f) => (
          <div
            key={f.key}
            className="flex items-start justify-between gap-4 border border-line bg-white px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium text-ink">{f.name}</span>
                <code className="font-mono text-[11px] text-ink/45">{f.key}</code>
                {f.rollout === 'beta' && <span className="badge-amber">beta</span>}
                {f.rollout === 'all' && <span className="badge-green">all users</span>}
                {f.rollout === 'off' && <span className="badge-ink">off</span>}
              </div>
              <div className="mt-1 text-[12px] leading-snug text-ink/55">{f.description}</div>
            </div>
            <Toggle
              checked={!!state[f.key]}
              onChange={(v) => setState((s) => ({ ...s, [f.key]: v }))}
              label=""
            />
          </div>
        ))}
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Системный баннер.
// ────────────────────────────────────────────────────────────────────────

type BannerDraft = {
  enabled: boolean;
  tone: 'info' | 'warn' | 'success';
  message: string;
  dismissible: boolean;
};

function BannerSection({ settings }: { settings: SettingsState }) {
  const baseline: BannerDraft = {
    enabled: Boolean(settings.get(BANNER_FIELDS.enabled)),
    tone: (settings.get<string>(BANNER_FIELDS.tone) as BannerDraft['tone']) || 'info',
    message: settings.get<string>(BANNER_FIELDS.message) || '',
    dismissible: Boolean(settings.get(BANNER_FIELDS.dismissible)),
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="banner"
      eyebrow="Объявления"
      title="Системный баннер"
      description="Плашка сверху во всех кабинетах арендаторов. Используйте для запланированных работ, новых фич и аварийных уведомлений."
      icon={Bell}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          BANNER_FIELDS as Record<keyof BannerDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('banner', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved(draft);
      }}
    >
      <div className="mb-5">
        <Toggle
          checked={draft.enabled}
          onChange={(enabled) => set({ enabled })}
          label="Показывать баннер"
          description="После включения баннер появится у всех арендаторов в течение 30 секунд."
        />
      </div>

      <FieldsGrid>
        <Field label="Тональность">
          <Segmented
            value={draft.tone}
            onChange={(tone) => set({ tone })}
            options={[
              { value: 'info', label: 'Инфо' },
              { value: 'warn', label: 'Внимание' },
              { value: 'success', label: 'Хорошая новость' },
            ]}
          />
        </Field>
        <Field label="Можно ли закрыть">
          <Toggle
            checked={draft.dismissible}
            onChange={(dismissible) => set({ dismissible })}
            label="Крестик закрытия"
            description="Пользователь скрывает баннер локально до следующего изменения."
          />
        </Field>
        <Field label="Текст баннера" className="md:col-span-2">
          <textarea
            className="input min-h-[86px] resize-y"
            maxLength={240}
            placeholder="Пример: 20 апреля с 03:00 до 04:00 МСК будут технические работы — меню и касса продолжат принимать заказы офлайн."
            value={draft.message}
            onChange={(e) => set({ message: e.target.value })}
          />
        </Field>
      </FieldsGrid>

      <div className="mt-5">
        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink/55">
          Предпросмотр
        </div>
        <div
          className={`mt-2 border px-4 py-3 text-[13px] ${
            draft.tone === 'warn'
              ? 'border-amber-600/30 bg-amber-50 text-amber-800'
              : draft.tone === 'success'
                ? 'border-green-600/30 bg-green-50 text-green-700'
                : 'border-ink/15 bg-paper text-ink/80'
          } ${draft.enabled ? '' : 'opacity-50'}`}
        >
          {draft.message.trim() || 'Текст баннера появится здесь.'}
        </div>
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Интеграции.
// ────────────────────────────────────────────────────────────────────────

type IntegrationsDraft = {
  apnsKeyId: string;
  apnsTeamId: string;
  apnsBundleId: string;
  telegramToken: string;
  telegramChatId: string;
  s3Endpoint: string;
  s3Bucket: string;
  s3Region: string;
  s3AccessKey: string;
  s3SecretKey: string;
  sentryDsn: string;
};

function IntegrationsSection({ settings }: { settings: SettingsState }) {
  const baseline: IntegrationsDraft = {
    apnsKeyId: settings.get<string>(INTEGRATIONS_FIELDS.apnsKeyId) || '',
    apnsTeamId: settings.get<string>(INTEGRATIONS_FIELDS.apnsTeamId) || '',
    apnsBundleId: settings.get<string>(INTEGRATIONS_FIELDS.apnsBundleId) || '',
    telegramToken: '',
    telegramChatId: settings.get<string>(INTEGRATIONS_FIELDS.telegramChatId) || '',
    s3Endpoint: settings.get<string>(INTEGRATIONS_FIELDS.s3Endpoint) || '',
    s3Bucket: settings.get<string>(INTEGRATIONS_FIELDS.s3Bucket) || '',
    s3Region: settings.get<string>(INTEGRATIONS_FIELDS.s3Region) || '',
    s3AccessKey: settings.get<string>(INTEGRATIONS_FIELDS.s3AccessKey) || '',
    s3SecretKey: '',
    sentryDsn: '',
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="integrations"
      eyebrow="Внешние сервисы"
      title="Интеграции"
      description="Ключи для push-уведомлений в iOS-кассу, Telegram-бота поддержки, хранилища файлов и трекинга ошибок."
      icon={Plug}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          INTEGRATIONS_FIELDS as Record<keyof IntegrationsDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('integrations', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved({ ...draft, telegramToken: '', s3SecretKey: '', sentryDsn: '' });
      }}
    >
      <div className="space-y-6">
        <IntegrationCard
          title="Apple Push Notifications"
          description="Для пушей кассе (новый заказ, закрытие смены). Нужен .p8-ключ из Apple Developer."
        >
          <FieldsGrid cols={3}>
            <Field label="Key ID">
              <input
                className="input font-mono text-[12.5px]"
                value={draft.apnsKeyId}
                onChange={(e) => set({ apnsKeyId: e.target.value })}
                placeholder="ABCDEF1234"
              />
            </Field>
            <Field label="Team ID">
              <input
                className="input font-mono text-[12.5px]"
                value={draft.apnsTeamId}
                onChange={(e) => set({ apnsTeamId: e.target.value })}
                placeholder="1234567890"
              />
            </Field>
            <Field label="Bundle ID">
              <input
                className="input font-mono text-[12.5px]"
                value={draft.apnsBundleId}
                onChange={(e) => set({ apnsBundleId: e.target.value })}
              />
            </Field>
          </FieldsGrid>
        </IntegrationCard>

        <IntegrationCard
          title="Telegram-бот поддержки"
          description="Бот получает тикеты от арендаторов и алерты об инцидентах платформы."
        >
          <FieldsGrid>
            <Field label="Bot token">
              <SecretInput
                value={draft.telegramToken}
                onChange={(telegramToken) => set({ telegramToken })}
                placeholder="123456:ABC-DEF..."
                currentMask={
                  settings.isMasked(INTEGRATIONS_FIELDS.telegramToken)
                    ? String(settings.get(INTEGRATIONS_FIELDS.telegramToken))
                    : null
                }
              />
            </Field>
            <Field label="Chat ID для алертов" hint="Например, −100123456789 для супергруппы.">
              <input
                className="input font-mono text-[12.5px]"
                placeholder="-100…"
                value={draft.telegramChatId}
                onChange={(e) => set({ telegramChatId: e.target.value })}
              />
            </Field>
          </FieldsGrid>
        </IntegrationCard>

        <IntegrationCard
          title="S3-совместимое хранилище"
          description="Картинки меню, логотипы арендаторов и экспорты отчётов."
        >
          <FieldsGrid cols={3}>
            <Field label="Endpoint">
              <input
                className="input"
                value={draft.s3Endpoint}
                onChange={(e) => set({ s3Endpoint: e.target.value })}
              />
            </Field>
            <Field label="Bucket">
              <input
                className="input"
                value={draft.s3Bucket}
                onChange={(e) => set({ s3Bucket: e.target.value })}
              />
            </Field>
            <Field label="Регион">
              <input
                className="input"
                value={draft.s3Region}
                onChange={(e) => set({ s3Region: e.target.value })}
              />
            </Field>
            <Field label="Access Key">
              <input
                className="input font-mono text-[12.5px]"
                value={draft.s3AccessKey}
                onChange={(e) => set({ s3AccessKey: e.target.value })}
              />
            </Field>
            <Field label="Secret Key" className="md:col-span-2">
              <SecretInput
                value={draft.s3SecretKey}
                onChange={(s3SecretKey) => set({ s3SecretKey })}
                placeholder="YCS..."
                currentMask={
                  settings.isMasked(INTEGRATIONS_FIELDS.s3SecretKey)
                    ? String(settings.get(INTEGRATIONS_FIELDS.s3SecretKey))
                    : null
                }
              />
            </Field>
          </FieldsGrid>
        </IntegrationCard>

        <IntegrationCard
          title="Sentry"
          description="Сбор ошибок backend и admin-панели. DSN серверной части."
        >
          <FieldsGrid>
            <Field label="DSN" className="md:col-span-2">
              <SecretInput
                value={draft.sentryDsn}
                onChange={(sentryDsn) => set({ sentryDsn })}
                placeholder="https://…@o123.ingest.sentry.io/456"
                currentMask={
                  settings.isMasked(INTEGRATIONS_FIELDS.sentryDsn)
                    ? String(settings.get(INTEGRATIONS_FIELDS.sentryDsn))
                    : null
                }
              />
            </Field>
          </FieldsGrid>
        </IntegrationCard>
      </div>
    </SettingsSection>
  );
}

function IntegrationCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="border border-line bg-paper/40 p-5">
      <div className="mb-4">
        <div className="text-[13px] font-medium text-ink">{title}</div>
        <div className="mt-1 text-[12px] leading-snug text-ink/55">{description}</div>
      </div>
      {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Бренд и домены.
// ────────────────────────────────────────────────────────────────────────

type BrandingDraft = {
  brandName: string;
  brandColor: string;
  landingUrl: string;
  adminUrl: string;
  menuRoot: string;
  logoLight: string;
  logoDark: string;
};

function BrandingSection({ settings }: { settings: SettingsState }) {
  const baseline: BrandingDraft = {
    brandName: settings.get<string>(BRANDING_FIELDS.brandName) || '',
    brandColor: settings.get<string>(BRANDING_FIELDS.brandColor) || '#E85624',
    landingUrl: settings.get<string>(BRANDING_FIELDS.landingUrl) || '',
    adminUrl: settings.get<string>(BRANDING_FIELDS.adminUrl) || '',
    menuRoot: settings.get<string>(BRANDING_FIELDS.menuRoot) || '',
    logoLight: settings.get<string>(BRANDING_FIELDS.logoLight) || '',
    logoDark: settings.get<string>(BRANDING_FIELDS.logoDark) || '',
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="branding"
      eyebrow="Внешний вид"
      title="Бренд и домены"
      description="Публичные адреса и бренд-айдентика. Используется в SSO-редиректах, письмах и QR-меню."
      icon={Globe2}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          BRANDING_FIELDS as Record<keyof BrandingDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('branding', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved(draft);
      }}
    >
      <FieldsGrid cols={2}>
        <Field label="Название бренда">
          <input
            className="input"
            value={draft.brandName}
            onChange={(e) => set({ brandName: e.target.value })}
          />
        </Field>
        <Field label="Акцентный цвет">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={draft.brandColor}
              onChange={(e) => set({ brandColor: e.target.value })}
              className="h-10 w-14 cursor-pointer border border-line p-0"
            />
            <input
              className="input flex-1 font-mono text-[13px]"
              value={draft.brandColor}
              onChange={(e) => set({ brandColor: e.target.value })}
            />
          </div>
        </Field>
        <Field label="Лендинг">
          <input
            className="input"
            value={draft.landingUrl}
            onChange={(e) => set({ landingUrl: e.target.value })}
          />
        </Field>
        <Field label="Админ-панель">
          <input
            className="input"
            value={draft.adminUrl}
            onChange={(e) => set({ adminUrl: e.target.value })}
          />
        </Field>
        <Field
          label="Корневой домен QR-меню"
          hint="Поддомены арендаторов: coffee.menu.resstro.ru"
          className="md:col-span-2"
        >
          <input
            className="input"
            value={draft.menuRoot}
            onChange={(e) => set({ menuRoot: e.target.value })}
          />
        </Field>
        <Field label="Логотип — URL (светлый фон)" hint="PNG/SVG, рекомендовано 240×80">
          <input
            className="input"
            value={draft.logoLight}
            onChange={(e) => set({ logoLight: e.target.value })}
            placeholder="https://cdn.resstro.ru/brand/logo-light.svg"
          />
        </Field>
        <Field label="Логотип — URL (тёмный фон)" hint="Версия для тёмной шапки / email-шапки">
          <input
            className="input"
            value={draft.logoDark}
            onChange={(e) => set({ logoDark: e.target.value })}
            placeholder="https://cdn.resstro.ru/brand/logo-dark.svg"
          />
        </Field>
      </FieldsGrid>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Юр. документы.
// ────────────────────────────────────────────────────────────────────────

type LegalDraft = {
  offerUrl: string;
  privacyUrl: string;
  consentUrl: string;
  companyName: string;
  companyInn: string;
};

function LegalSection({ settings }: { settings: SettingsState }) {
  const baseline: LegalDraft = {
    offerUrl: settings.get<string>(LEGAL_FIELDS.offerUrl) || '',
    privacyUrl: settings.get<string>(LEGAL_FIELDS.privacyUrl) || '',
    consentUrl: settings.get<string>(LEGAL_FIELDS.consentUrl) || '',
    companyName: settings.get<string>(LEGAL_FIELDS.companyName) || '',
    companyInn: settings.get<string>(LEGAL_FIELDS.companyInn) || '',
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="legal"
      eyebrow="Документы"
      title="Юридическая информация"
      description="Ссылки в футере лендинга и реквизиты юридического лица в чеках и договоре-оферте."
      icon={FileText}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          LEGAL_FIELDS as Record<keyof LegalDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('legal', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved(draft);
      }}
    >
      <FieldsGrid cols={3}>
        <Field label="Оферта">
          <input
            className="input"
            value={draft.offerUrl}
            onChange={(e) => set({ offerUrl: e.target.value })}
          />
        </Field>
        <Field label="Политика конфиденциальности">
          <input
            className="input"
            value={draft.privacyUrl}
            onChange={(e) => set({ privacyUrl: e.target.value })}
          />
        </Field>
        <Field label="Согласие на обработку данных">
          <input
            className="input"
            value={draft.consentUrl}
            onChange={(e) => set({ consentUrl: e.target.value })}
          />
        </Field>
        <Field label="Юрлицо / ИП" className="md:col-span-2">
          <input
            className="input"
            value={draft.companyName}
            onChange={(e) => set({ companyName: e.target.value })}
          />
        </Field>
        <Field label="ИНН">
          <input
            className="input font-mono text-[13px]"
            value={draft.companyInn}
            onChange={(e) => set({ companyInn: e.target.value })}
          />
        </Field>
      </FieldsGrid>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Поддержка.
// ────────────────────────────────────────────────────────────────────────

type SupportDraft = {
  email: string;
  whatsapp: string;
  phone: string;
  sla: '1h' | '4h' | '24h';
  weekendMode: boolean;
};

function SupportSection({ settings }: { settings: SettingsState }) {
  const baseline: SupportDraft = {
    email: settings.get<string>(SUPPORT_FIELDS.email) || '',
    whatsapp: settings.get<string>(SUPPORT_FIELDS.whatsapp) || '',
    phone: settings.get<string>(SUPPORT_FIELDS.phone) || '',
    sla: (settings.get<string>(SUPPORT_FIELDS.sla) as SupportDraft['sla']) || '4h',
    weekendMode: Boolean(settings.get(SUPPORT_FIELDS.weekendMode)),
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="support"
      eyebrow="Контакты"
      title="Поддержка арендаторов"
      description="Эти контакты видны в футере лендинга, в подписи писем и в разделе «Помощь» админ-панели."
      icon={MessageSquare}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          SUPPORT_FIELDS as Record<keyof SupportDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('support', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved(draft);
      }}
    >
      <FieldsGrid cols={3}>
        <Field label="Email">
          <input
            className="input"
            value={draft.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Field>
        <Field label="WhatsApp">
          <input
            className="input"
            value={draft.whatsapp}
            onChange={(e) => set({ whatsapp: e.target.value })}
          />
        </Field>
        <Field label="Телефон">
          <input
            className="input"
            value={draft.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </Field>
        <Field label="SLA по первому ответу">
          <Segmented
            value={draft.sla}
            onChange={(sla) => set({ sla })}
            options={[
              { value: '1h', label: '1 час' },
              { value: '4h', label: '4 часа' },
              { value: '24h', label: '1 рабочий день' },
            ]}
          />
        </Field>
        <Field
          label="Дежурство в выходные"
          hint="Отвечаем в субботу и воскресенье с 10:00 до 20:00."
          className="md:col-span-2"
        >
          <Toggle
            checked={draft.weekendMode}
            onChange={(weekendMode) => set({ weekendMode })}
            label={draft.weekendMode ? 'Включено' : 'Только будни'}
          />
        </Field>
      </FieldsGrid>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Безопасность.
// ────────────────────────────────────────────────────────────────────────

type SecurityDraft = {
  sessionTtlDays: number;
  adminSessionTtlMin: number;
  publicRps: number;
  operatorRps: number;
  ipAllowlist: string;
  mfaRequired: boolean;
  auditRetentionDays: number;
};

function SecuritySection({ settings }: { settings: SettingsState }) {
  const baseline: SecurityDraft = {
    sessionTtlDays: Number(settings.get(SECURITY_FIELDS.sessionTtlDays)) || 30,
    adminSessionTtlMin: Number(settings.get(SECURITY_FIELDS.adminSessionTtlMin)) || 60,
    publicRps: Number(settings.get(SECURITY_FIELDS.publicRps)) || 10,
    operatorRps: Number(settings.get(SECURITY_FIELDS.operatorRps)) || 5,
    ipAllowlist: settings.get<string>(SECURITY_FIELDS.ipAllowlist) || '',
    mfaRequired: Boolean(settings.get(SECURITY_FIELDS.mfaRequired)),
    auditRetentionDays: Number(settings.get(SECURITY_FIELDS.auditRetentionDays)) || 365,
  };
  const { draft, set, dirty, markSaved } = useSectionDraft(baseline, settings.version);

  return (
    <SettingsSection
      id="security"
      eyebrow="Доступ и лимиты"
      title="Безопасность"
      description="Сессии, rate-limits, IP-allowlist для супер-админа и ретеншн аудит-лога."
      icon={ShieldCheck}
      dirty={dirty}
      onSave={async () => {
        const entries = buildEntries(
          draft,
          baseline,
          SECURITY_FIELDS as Record<keyof SecurityDraft, FieldDef>,
          settings,
        );
        const r = await settings.saveSection('security', entries);
        if (!r.ok) throw new Error(r.error ?? 'Ошибка сохранения');
        markSaved(draft);
      }}
      footerHint="Изменения применяются к новым токенам. Текущие сессии работают до истечения."
    >
      <FieldsGrid cols={3}>
        <Field label="Сессия пользователя (дней)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.sessionTtlDays)}
            onChange={(e) => set({ sessionTtlDays: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Сессия оператора sadmin (мин)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.adminSessionTtlMin)}
            onChange={(e) => set({ adminSessionTtlMin: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Retention аудит-лога (дней)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.auditRetentionDays)}
            onChange={(e) => set({ auditRetentionDays: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Rate-limit /api/public/* (rps на IP)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.publicRps)}
            onChange={(e) => set({ publicRps: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Rate-limit /api/operator/* (rps на IP)">
          <input
            className="input"
            inputMode="numeric"
            value={String(draft.operatorRps)}
            onChange={(e) => set({ operatorRps: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="2FA для операторов платформы">
          <Toggle
            checked={draft.mfaRequired}
            onChange={(mfaRequired) => set({ mfaRequired })}
            label={draft.mfaRequired ? 'Требуется' : 'По желанию'}
          />
        </Field>
        <Field
          label="IP allowlist для sadmin"
          hint="Список CIDR через запятую или перенос строки. Пусто — доступ отовсюду."
          className="md:col-span-3"
        >
          <textarea
            className="input min-h-[86px] resize-y font-mono text-[12.5px]"
            placeholder="203.0.113.0/24, 198.51.100.42"
            value={draft.ipAllowlist}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => set({ ipAllowlist: e.target.value })}
          />
        </Field>
      </FieldsGrid>

      <div className="mt-6">
        <Callout tone="warn">
          <strong>Осторожно:</strong> если в IP allowlist добавить только IP, отличный от
          текущего — вы потеряете доступ к sadmin. Проверяйте себя перед сохранением.
        </Callout>
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Расширенные параметры (сырой key-value store).
// ────────────────────────────────────────────────────────────────────────

function AdvancedSection({ settings }: { settings: SettingsState }) {
  const { raw, meta, reload, loading } = settings;
  const [settingsList, setSettingsList] = useState<PlatformSetting[]>([]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Собираем сырой список из общего хука, плюс сортируем по ключу.
  useEffect(() => {
    const list: PlatformSetting[] = [];
    for (const [k, v] of raw.entries()) {
      const m = meta.get(k);
      list.push({
        key: k,
        value: v,
        isSecret: m?.isSecret ?? false,
        updatedAt: m?.updatedAt ?? new Date().toISOString(),
        updatedBy: m?.updatedBy ?? null,
      });
    }
    list.sort((a, b) => a.key.localeCompare(b.key));
    setSettingsList(list);
  }, [raw, meta]);

  const parseValue = (rawInput: string): unknown => {
    const trimmed = rawInput.trim();
    if (!trimmed) return '';
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  };

  const handleAdd = async () => {
    if (!newKey.trim()) return;
    try {
      setSavingKey(newKey);
      setError(null);
      await upsertSetting(newKey.trim(), parseValue(newValue));
      setNewKey('');
      setNewValue('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm(`Удалить настройку "${key}"?`)) return;
    try {
      setSavingKey(key);
      await deleteSetting(key);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setSavingKey(null);
    }
  };

  const trackedCount = settingsList.filter((s) => isTrackedKey(s.key)).length;
  const rawCount = settingsList.length - trackedCount;

  return (
    <SettingsSection
      id="advanced"
      eyebrow="Сырой доступ"
      title="Расширенные параметры"
      description="Произвольные key-value, если фичи ещё нет в интерфейсе выше. Namespacing: banner.*, feature.*, yukassa.*, kv.*. Секретные префиксы маскируются автоматически."
      icon={KeyRound}
      footerHint={
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[12px] text-ink/60 hover:text-ink"
        >
          <RefreshCw size={12} strokeWidth={1.75} className={loading ? 'animate-spin' : ''} />
          Обновить
        </button>
      }
    >
      {error && (
        <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-[minmax(220px,1fr)_2fr_auto]">
        <input
          className="input"
          placeholder="ключ (например feature.cashier.apple_pay)"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
        <input
          className="input font-mono text-[13px]"
          placeholder="значение (JSON или строка)"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary"
          onClick={handleAdd}
          disabled={!newKey.trim() || savingKey !== null}
        >
          <Plus size={14} strokeWidth={1.75} />
          Добавить
        </button>
      </div>

      <div className="border border-line">
        <div className="flex flex-col gap-1 border-b border-line px-4 py-3 md:flex-row md:items-center md:justify-between md:px-5">
          <h3 className="text-[13px] font-medium text-ink">Все параметры</h3>
          <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-ink/45">
            {settingsList.length} · из них {rawCount} произвольных
          </span>
        </div>

        {/* Desktop */}
        <table className="table-grid hidden md:table">
          <thead>
            <tr>
              <th>Ключ</th>
              <th>Значение</th>
              <th>Кем обновлено</th>
              <th className="text-right">Когда</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {settingsList.map((s) => (
              <tr key={s.key}>
                <td className="font-mono text-[12px]">
                  {s.key}
                  {s.isSecret && (
                    <span className="ml-2 inline-block bg-ink px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wider text-white">
                      secret
                    </span>
                  )}
                  {isTrackedKey(s.key) && (
                    <span className="ml-2 inline-block border border-line bg-paper px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wider text-ink/55">
                      управляется UI выше
                    </span>
                  )}
                </td>
                <td className="font-mono text-[12px] text-ink/80">{formatValue(s.value)}</td>
                <td className="font-mono text-[11px] text-ink/50">
                  {s.updatedBy ? s.updatedBy.slice(0, 10) : '—'}
                </td>
                <td className="text-right font-mono text-[12px] text-ink/60">
                  {formatDateTime(s.updatedAt)}
                </td>
                <td className="text-right">
                  <button
                    className="text-[12px] text-red-600 hover:underline"
                    onClick={() => handleDelete(s.key)}
                    disabled={savingKey === s.key}
                  >
                    <Trash2 size={12} className="mr-1 inline" />
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
            {!loading && settingsList.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-ink/50">
                  Настроек пока нет
                </td>
              </tr>
            )}
            {loading && settingsList.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-ink/40">
                  <span className="inline-flex items-center gap-2">
                    <Gauge size={13} strokeWidth={1.75} />
                    Загрузка…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Mobile */}
        <ul className="divide-y divide-line md:hidden">
          {settingsList.map((s) => (
            <li key={s.key} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="break-all font-mono text-[12px] text-ink">{s.key}</span>
                {s.isSecret && (
                  <span className="inline-block bg-ink px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wider text-white">
                    secret
                  </span>
                )}
                {isTrackedKey(s.key) && (
                  <span className="inline-block border border-line bg-paper px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wider text-ink/55">
                    UI выше
                  </span>
                )}
              </div>
              <div className="mt-1.5 break-all font-mono text-[12px] text-ink/80">
                {formatValue(s.value)}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 font-mono text-[11px] text-ink/50">
                <span>{formatDateTime(s.updatedAt)}</span>
                <button
                  className="inline-flex items-center gap-1 text-[12px] font-sans text-red-600 hover:underline"
                  onClick={() => handleDelete(s.key)}
                  disabled={savingKey === s.key}
                >
                  <Trash2 size={12} />
                  Удалить
                </button>
              </div>
            </li>
          ))}
          {!loading && settingsList.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/50">Настроек пока нет</li>
          )}
          {loading && settingsList.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
          )}
        </ul>
      </div>
    </SettingsSection>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Вспомогательные элементы.
// ────────────────────────────────────────────────────────────────────────

function FieldMeta({
  field,
  settings,
}: {
  field: FieldDef;
  settings: SettingsState;
}) {
  const m = settings.meta.get(field.key);
  if (!m) return <>Не сохранено</>;
  return (
    <>
      Обновлено {formatDateTime(m.updatedAt)}
      {m.updatedBy ? ` · ${m.updatedBy.slice(0, 8)}` : ''}
    </>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
